// Radar display for departure/approach/center: stereographic map around the aerodrome, extended centre lines
// with range marks, fixes, control zone, minimum vectoring areas; targets at the sensor update rate with
// history dots, speed vector and three-line labels (callsign · level/trend/cleared level · ground speed/type).
import type { GameSession } from '../../game/session';
import type { Aircraft } from '../../sim/aircraft';
import { View, attachPanZoom } from './view';
import { C } from './colors';
import { NM, DEG, bearing, dist } from '../../sim/geo';

export class RadarScope {
  readonly view = new View();
  private readonly ctx: CanvasRenderingContext2D;
  private hits: { cs: string; x: number; y: number; lx: number; ly: number; lw: number; lh: number }[] = [];
  private detach: () => void;
  private mouse: { x: number; y: number } | null = null;
  private rbl: { from: { x: number; y: number }; to: { x: number; y: number } } | null = null;
  vectorMin = 1;
  showMva = false;
  showInfo = true;
  onSelect: (cs: string | null) => void = () => {};
  /** Right click on the map with an aircraft selected: heading towards the point. */
  onHeadingTo: (cs: string, hdgMag: number) => void = () => {};
  onContext: (cs: string | null, sx: number, sy: number) => void = () => {};

  private autoFit = true;
  /** Initial view: range (NM to the nearest edge) and centre; kept on resize until the user zooms or pans. */
  home = { rangeNm: 40, cx: 0, cy: 0 };
  constructor(private readonly canvas: HTMLCanvasElement, private readonly s: GameSession, rangeNm = 40) {
    this.ctx = canvas.getContext('2d')!;
    this.home.rangeNm = rangeNm;
    this.view.fit(canvas);
    this.applyHome();
    this.detach = attachPanZoom(canvas, this.view, {
      min: 0.0004, max: 0.06,
      onInteract: () => { this.autoFit = false; },
      onClick: (x, y, e) => {
        if (e.shiftKey) { const p = this.view.toWorld(x, y); this.rbl = this.rbl && !this.rbl.to ? null : { from: p, to: p }; return; }
        this.onSelect(this.pick(x, y));
      },
      onContext: (x, y) => {
        const cs = this.pick(x, y);
        if (cs) { this.onContext(cs, x, y); return; }
        const sel = this.s.selected ? this.s.world.ac(this.s.selected) : null;
        if (sel && !sel.onGround) {
          const p = this.view.toWorld(x, y);
          const brgTrue = bearing(sel.x, sel.y, p.x, p.y);
          const mag = Math.round(this.s.world.trueToMag(brgTrue) / 5) * 5 || 360;
          this.onHeadingTo(sel.cs, mag);
        }
      },
      onMove: (x, y) => { this.mouse = { x, y }; if (this.rbl) this.rbl.to = this.view.toWorld(x, y); },
    });
  }
  dispose(): void { this.detach(); }
  applyHome(): void {
    this.view.scale = Math.min(this.view.w, this.view.h) / (this.home.rangeNm * 2 * NM);
    this.view.cx = this.home.cx; this.view.cy = this.home.cy;
  }
  clearRbl(): void { this.rbl = null; }

  draw(): void {
    const { ctx, view } = this;
    if (view.fit(this.canvas) && this.autoFit) this.applyHome();
    ctx.setTransform(view.dpr, 0, 0, view.dpr, 0, 0);
    ctx.fillStyle = C.bg;
    ctx.fillRect(0, 0, view.w, view.h);
    this.drawMap();
    this.hits = [];
    const w = this.s.world;
    for (const a of w.aircraft) if (!a.onGround && a.altFt - w.meta.elevFt > 150) this.drawTarget(a);
    this.drawRbl();
    if (this.showInfo) this.drawInfo();
  }

  private drawMap(): void {
    const { ctx, view } = this;
    const w = this.s.world, meta = w.meta, ap = this.s.airport;
    const P = (x: number, y: number) => view.toScreen(x, y);
    ctx.lineWidth = 1;
    // range rings every 10 NM (very faint)
    ctx.strokeStyle = '#1f282d';
    const [ox, oy] = P(0, 0);
    for (let r = 10; r <= 100; r += 10) { ctx.beginPath(); ctx.arc(ox, oy, r * NM * view.scale, 0, Math.PI * 2); ctx.stroke(); }
    // MVA
    if (this.showMva) {
      ctx.setLineDash([2, 4]); ctx.strokeStyle = '#3a3329'; ctx.fillStyle = '#5a4d3a'; ctx.font = '10px "IBM Plex Mono", monospace';
      for (const m of meta.mva) {
        if (m.radiusNm > 200) continue;
        ctx.beginPath(); ctx.arc(ox, oy, m.radiusNm * NM * view.scale, 0, Math.PI * 2); ctx.stroke();
        ctx.fillText(String(m.ft / 100).padStart(2, '0'), ox + m.radiusNm * NM * view.scale * 0.71 + 3, oy - m.radiusNm * NM * view.scale * 0.71);
      }
      ctx.setLineDash([]);
    }
    // airspace
    for (const a of meta.airspace) {
      ctx.strokeStyle = C.ctr; ctx.setLineDash([6, 4]);
      ctx.beginPath();
      a.poly.forEach(([lat, lon], i) => { const p = ap.proj.toXY(lat, lon); const [x, y] = P(p.x, p.y); if (i) ctx.lineTo(x, y); else ctx.moveTo(x, y); });
      ctx.closePath(); ctx.stroke(); ctx.setLineDash([]);
    }
    // sector circles of the player's radar units
    for (const u of meta.units) {
      if (!u.sector || !w.playerUnits().includes(u.id)) continue;
      ctx.strokeStyle = '#2b363d'; ctx.setLineDash([10, 6]);
      ctx.beginPath(); ctx.arc(ox, oy, u.sector.radiusNm * NM * view.scale, 0, Math.PI * 2); ctx.stroke(); ctx.setLineDash([]);
    }
    // STAR legs and SID exits
    ctx.strokeStyle = '#26323a';
    for (const st of meta.stars) {
      ctx.beginPath();
      st.legs.forEach((l, i) => { const p = w.fixPos(l.fix); if (!p) return; const [x, y] = P(p.x, p.y); if (i) ctx.lineTo(x, y); else ctx.moveTo(x, y); });
      ctx.stroke();
    }
    // runways and extended centre lines with 1/5 NM marks
    for (const rw of ap.runways) {
      const a = rw.ends[rw.endIds[0]]!, b = rw.ends[rw.endIds[1]]!;
      const [x1, y1] = P(a.start.x, a.start.y), [x2, y2] = P(b.start.x, b.start.y);
      ctx.strokeStyle = '#9aa6ae'; ctx.lineWidth = 3; ctx.beginPath(); ctx.moveTo(x1, y1); ctx.lineTo(x2, y2); ctx.stroke(); ctx.lineWidth = 1;
    }
    for (const endId of w.config.arr) {
      const e = ap.runwayEnds[endId];
      if (!e) continue;
      const crs = e.meta.ils?.course ?? e.hdg;
      const ux = -Math.sin(crs * DEG), uy = -Math.cos(crs * DEG);
      const nx = uy, ny = -ux;
      ctx.strokeStyle = C.centreLineExt;
      const [tx, ty] = P(e.thr.x, e.thr.y);
      const [fx, fy] = P(e.thr.x + ux * 20 * NM, e.thr.y + uy * 20 * NM);
      ctx.beginPath(); ctx.moveTo(tx, ty); ctx.lineTo(fx, fy); ctx.stroke();
      for (let d = 1; d <= 20; d++) {
        const len = d % 5 === 0 ? 600 : 250;
        const cx = e.thr.x + ux * d * NM, cy = e.thr.y + uy * d * NM;
        const [ax, ay] = P(cx + nx * len, cy + ny * len), [bx, by] = P(cx - nx * len, cy - ny * len);
        ctx.beginPath(); ctx.moveTo(ax, ay); ctx.lineTo(bx, by); ctx.stroke();
      }
    }
    // fixes
    ctx.font = '10px "IBM Plex Mono", monospace';
    for (const [name, f] of Object.entries(meta.fixes)) {
      const p = ap.proj.toXY(f.lat, f.lon);
      const [x, y] = P(p.x, p.y);
      if (x < -40 || y < -40 || x > view.w + 40 || y > view.h + 40) continue;
      ctx.strokeStyle = C.fix; ctx.fillStyle = C.mapText;
      ctx.beginPath();
      if (f.type === 'vor' || f.type === 'ndb') { ctx.rect(x - 3.5, y - 3.5, 7, 7); }
      else { ctx.moveTo(x, y - 4); ctx.lineTo(x + 3.5, y + 3); ctx.lineTo(x - 3.5, y + 3); ctx.closePath(); }
      ctx.stroke();
      ctx.fillText(name, x + 6, y + 3);
    }
    // holding patterns at the IAFs
    ctx.strokeStyle = '#2d3a42';
    for (const h of meta.holdings) {
      const p = w.fixPos(h.fix);
      if (!p) continue;
      const inb = w.magToTrue(h.inboundCourse);
      const r = 1.5 * NM, len = 4 * NM;
      const ox2 = -Math.sin(inb * DEG) * len, oy2 = -Math.cos(inb * DEG) * len;
      const side = h.turn === 'R' ? 1 : -1;
      const nx = Math.cos(inb * DEG) * 2 * r * side, ny = -Math.sin(inb * DEG) * 2 * r * side;
      const pts = [[p.x, p.y], [p.x + ox2, p.y + oy2], [p.x + ox2 + nx, p.y + oy2 + ny], [p.x + nx, p.y + ny]] as const;
      ctx.beginPath();
      pts.forEach(([x, y], i) => { const [sx, sy] = P(x, y); if (i) ctx.lineTo(sx, sy); else ctx.moveTo(sx, sy); });
      ctx.closePath(); ctx.stroke();
    }
  }

  private colour(a: Aircraft): string {
    const w = this.s.world;
    if (w.safety.stca.some(c => !c.predicted && (c.a === a.cs || c.b === a.cs)) || w.safety.msaw.has(a.cs)) return C.alert;
    if (this.s.level.assists.conflictPreview && w.safety.stca.some(c => c.predicted && (c.a === a.cs || c.b === a.cs))) return C.warn;
    if (this.s.selected === a.cs) return C.selected;
    const p = w.pilot(a.cs);
    const mine = !!p?.freq && w.playerUnits().includes(p.freq);
    if (mine && w.requests.some(r => r.cs === a.cs && w.playerUnits().includes(r.unit))) return C.calling;
    if (mine) return C.mine;
    return C.other;
  }

  private drawTarget(a: Aircraft): void {
    const { ctx, view } = this;
    const w = this.s.world;
    const col = this.colour(a);
    const r = a.radar;
    if (r.t < 0) return;
    // history dots
    ctx.fillStyle = col; ctx.globalAlpha = 0.45;
    for (const h of a.history) { const [hx, hy] = view.toScreen(h.x, h.y); ctx.fillRect(hx - 1.2, hy - 1.2, 2.4, 2.4); }
    ctx.globalAlpha = 1;
    const [x, y] = view.toScreen(r.x, r.y);
    // speed vector
    const vlen = (r.gs * this.vectorMin) / 60 * NM;
    const [vx, vy] = view.toScreen(r.x + Math.sin(r.trk * DEG) * vlen, r.y + Math.cos(r.trk * DEG) * vlen);
    ctx.strokeStyle = col; ctx.lineWidth = 1;
    ctx.beginPath(); ctx.moveTo(x, y); ctx.lineTo(vx, vy); ctx.stroke();
    // symbol: SSR/Mode S target
    ctx.strokeStyle = col; ctx.lineWidth = 1.4;
    ctx.strokeRect(x - 3.5, y - 3.5, 7, 7);
    // label
    const p = w.pilot(a.cs);
    const qnh = w.weather.qnh;
    const aboveTa = a.altFt > w.meta.transitionAltFt + 50;
    const lvl = Math.round((aboveTa ? a.pressureAlt(qnh) : r.alt) / 100);
    const trend = a.vs > 300 ? '↑' : a.vs < -300 ? '↓' : ' ';
    const cflFt = a.tgtAltFt > w.meta.transitionAltFt ? a.tgtAltFt + (1013.25 - qnh) * 27.3 : a.tgtAltFt;
    const cfl = Math.abs(a.tgtAltFt - r.alt) > 150 ? String(Math.round(cflFt / 100)).padStart(3, '0') : '';
    const l1 = `${a.cs}${a.type.wake === 'H' ? ' H' : a.type.wake === 'J' ? ' J' : ''}`;
    const l2 = `${String(lvl).padStart(3, '0')}${trend}${cfl}`;
    const hdgInfo = a.amode === 'HDG' && a.tgtHdg !== null ? ` H${String(Math.round(w.trueToMag(a.tgtHdg)) || 360).padStart(3, '0')}` : a.locCap ? ' LOC' : a.amode === 'HOLD' ? ' HLD' : '';
    const l3 = `${String(Math.round(r.gs / 10)).padStart(2, '0')} ${a.type.icao}${a.atcSpeed ? ' S' + a.atcSpeed : ''}${hdgInfo}`;
    ctx.font = '500 11px "IBM Plex Mono", monospace';
    const lw = Math.max(ctx.measureText(l1).width, ctx.measureText(l2).width, ctx.measureText(l3).width) + 6;
    const lx = x + 18, ly = y - 44, lh = 40;
    ctx.globalAlpha = 0.55; ctx.beginPath(); ctx.moveTo(x + 4, y - 4); ctx.lineTo(lx, ly + lh - 4); ctx.stroke(); ctx.globalAlpha = 1;
    if (this.s.selected === a.cs) { ctx.strokeStyle = C.selected; ctx.lineWidth = 1; ctx.strokeRect(lx - 2, ly - 1, lw + 2, lh + 1); }
    ctx.fillStyle = col; ctx.textBaseline = 'top';
    ctx.fillText(l1, lx, ly + 1);
    ctx.fillText(l2, lx, ly + 14);
    ctx.fillStyle = col === C.other ? C.other : '#aab6be';
    ctx.fillText(l3, lx, ly + 27);
    ctx.textBaseline = 'alphabetic';
    // pending request marker
    if (p && w.requests.some(q => q.cs === a.cs && w.playerUnits().includes(q.unit))) { ctx.fillStyle = C.calling; ctx.beginPath(); ctx.arc(lx - 6, ly + 6, 2.5, 0, Math.PI * 2); ctx.fill(); }
    this.hits.push({ cs: a.cs, x, y, lx, ly, lw, lh });
  }

  private drawRbl(): void {
    if (!this.rbl) return;
    const { ctx, view } = this;
    const { from, to } = this.rbl;
    const [x1, y1] = view.toScreen(from.x, from.y), [x2, y2] = view.toScreen(to.x, to.y);
    ctx.strokeStyle = '#b9c4cb'; ctx.setLineDash([4, 3]); ctx.beginPath(); ctx.moveTo(x1, y1); ctx.lineTo(x2, y2); ctx.stroke(); ctx.setLineDash([]);
    const d = dist(from.x, from.y, to.x, to.y) / NM;
    const b = Math.round(this.s.world.trueToMag(bearing(from.x, from.y, to.x, to.y))) || 360;
    ctx.fillStyle = '#d5dde2'; ctx.font = '11px "IBM Plex Mono", monospace';
    ctx.fillText(`${String(b).padStart(3, '0')}° ${d.toFixed(1)} NM`, x2 + 8, y2 - 6);
  }

  private drawInfo(): void {
    const { ctx, view } = this;
    ctx.fillStyle = C.mapText; ctx.font = '10px "IBM Plex Mono", monospace';
    const rangeNm = Math.round(Math.min(view.w, view.h) / view.scale / NM / 2);
    ctx.fillText(`Bereich ${rangeNm} NM · Vektor ${this.vectorMin} min · Shift+Klick: Peilung/Entfernung · Rechtsklick: Steuerkurs`, 12, view.h - 10);
    if (this.mouse) {
      const p = view.toWorld(this.mouse.x, this.mouse.y);
      const b = Math.round(this.s.world.trueToMag(bearing(0, 0, p.x, p.y))) || 360;
      ctx.fillText(`${String(b).padStart(3, '0')}° ${(Math.hypot(p.x, p.y) / NM).toFixed(1)} NM`, view.w - 110, view.h - 10);
    }
  }

  pick(sx: number, sy: number): string | null {
    let best: string | null = null, bd = Infinity;
    for (const h of this.hits) {
      if (sx >= h.lx && sx <= h.lx + h.lw && sy >= h.ly && sy <= h.ly + h.lh) return h.cs;
      const d = Math.hypot(sx - h.x, sy - h.y);
      if (d < 14 && d < bd) { bd = d; best = h.cs; }
    }
    return best;
  }
}
